import { bundleSummary } from './bundles'
import type { Product } from './types'

/** The answer columns a product edit may touch. The rules allow exactly these. */
export type ProductEdit = Partial<Pick<Product,
  'keep' | 'familyFinal' | 'nameFinal' | 'skuFinal' | 'vendastaSku' | 'listPrice'
  | 'setupFee' | 'billing' | 'notes' | 'activity' | 'purpose' | 'benefits'
  | 'isBundle' | 'bundleItems'>>

const LABELS: Record<keyof ProductEdit, string> = {
  keep: 'Keep it?', familyFinal: 'Product family', nameFinal: 'Product name',
  skuFinal: 'SKU', vendastaSku: 'Vendasta SKU', listPrice: 'List price',
  setupFee: 'Setup fee', billing: 'Billing', notes: 'Notes',
  activity: 'Activity', purpose: 'Purpose', benefits: 'Benefits',
  isBundle: 'Bundle', bundleItems: 'What the bundle contains',
}

/** A product carrying one candidate value, so a members list can be read as prose. */
const withItems = (product: Product, items: string[]): Product =>
  ({ ...product, isBundle: true, bundleItems: items })

/**
 * How one field's value reads in the change log.
 *
 * Never a document id: the log is read months later by somebody asking why the catalogue
 * says what it says, and `added-POD-1-Podcast-sponsorship` answers nothing. The bundle
 * members are named, and the flag is written as the sentence it stands for.
 */
function describe(
  key: keyof ProductEdit, value: unknown, product: Product, all: Product[],
): string {
  if (key === 'isBundle') return value ? 'Sold as a bundle' : 'Not a bundle'
  if (key === 'bundleItems') {
    return bundleSummary(withItems(product, (value as string[]) ?? []), all)
  }
  return String(value ?? '')
}

/**
 * One entry per field that actually moved.
 *
 * The history reads as a list of changes rather than one opaque "edited", because the
 * question asked later is which answer changed and when — not that somebody saved the
 * form.
 */
export function productChanges(
  product: Product, edit: ProductEdit, all: Product[],
): Array<{ field: string; from: string; to: string }> {
  const out: Array<{ field: string; from: string; to: string }> = []
  for (const key of Object.keys(edit) as Array<keyof ProductEdit>) {
    const to = describe(key, edit[key], product, all)
    const from = describe(key, product[key], product, all)
    if (to === from) continue
    out.push({
      field: LABELS[key] ?? key,
      from: from || '(blank)',
      to: to || '(cleared)',
    })
  }
  return out
}
